import { useDrag } from "react-dnd";

import { DRAGGABLE_SLICE } from "../../widget.constants";
import { usePuzzle } from "../../widget.state";
import { PuzzleRoundStatusEnum } from "../../widget.types";

export function PuzzleSlice({
  isDraggable,
  sliceIndex,
  pendingIndex,
  arrangedIndex,
  backgroundSize,
  backgroundImage,
  backgroundPosition,
  status,
  setData,
  onStart,
}: {
  isDraggable: boolean;
  sliceIndex: number;
  pendingIndex?: number;
  arrangedIndex?: number;
  backgroundSize: string;
  backgroundImage: string;
  backgroundPosition: string;
  status: PuzzleRoundStatusEnum;
  setData: ReturnType<typeof usePuzzle>["setData"];
  onStart: () => void;
}) {
  const [{ canDrag, isDragging }, dragRef] = useDrag(
    () => ({
      canDrag: () => isDraggable,
      type: DRAGGABLE_SLICE,
      item: () => {
        if (status === PuzzleRoundStatusEnum.IDLE) {
          onStart();
        }
        return { sliceIndex, pendingIndex, arrangedIndex };
      },
      collect: (monitor) => ({
        handlerId: monitor.getHandlerId(),
        canDrag: monitor.canDrag(),
        isDragging: monitor.isDragging() && !monitor.didDrop(),
      }),
      end: (item, monitor) => {
        if (!item) return;

        const { targetArrangedIndex, targetPendingIndex } =
          monitor.getDropResult<{
            targetArrangedIndex?: number;
            targetPendingIndex?: number;
          }>() ?? {};

        if (Number(targetArrangedIndex) >= 0 && Number(pendingIndex) >= 0) {
          setData((data) => ({
            ...data,
            pendingSlices: data.pendingSlices.map((pendingSlice, pendingSliceI) =>
              pendingSliceI === pendingIndex ? null : pendingSlice
            ),
            arrangedSlices: data.arrangedSlices.map((arrangedSlice, arrangedSliceI) =>
              arrangedSliceI === targetArrangedIndex ? sliceIndex : arrangedSlice
            ),
          }));
        } else if (Number(targetArrangedIndex) >= 0 && Number(arrangedIndex) >= 0) {
          setData((data) => ({
            ...data,
            arrangedSlices: data.arrangedSlices.map((arrangedSlice, arrangedSliceI) =>
              arrangedSliceI === arrangedIndex
                ? null
                : arrangedSliceI === targetArrangedIndex
                ? sliceIndex
                : arrangedSlice
            ),
          }));
        } else if (Number(targetPendingIndex) >= 0 && Number(arrangedIndex) >= 0) {
          setData((data) => ({
            ...data,
            arrangedSlices: data.arrangedSlices.map((arrangedSlice, arrangedSliceI) =>
              arrangedSliceI === arrangedIndex ? null : arrangedSlice
            ),
            pendingSlices: data.pendingSlices.map((pendingSlice, pendingSliceI) =>
              pendingSliceI === targetPendingIndex ? sliceIndex : pendingSlice
            ),
          }));
        } else if (Number(targetPendingIndex) >= 0 && Number(pendingIndex) >= 0) {
          setData((data) => ({
            ...data,
            pendingSlices: data.pendingSlices.map(
              (pendingSlice, pendingSliceI) =>
                (pendingSliceI !== pendingIndex || null) &&
                ((pendingSliceI === targetPendingIndex && sliceIndex) || pendingSlice)
            ),
          }));
        }
      },
    }),
    [isDraggable, sliceIndex, pendingIndex, arrangedIndex]
  );

  const updatePuzzleSliceStyleOnStateChange = (): string =>
    [
      "arcade__puzzle__slice",
      canDrag && "arcade__puzzle__slice--draggable",
      isDragging && "arcade__puzzle__slice--dragging",
    ]
      .filter(Boolean)
      .join(" ");

  return (
    <div
      ref={dragRef}
      className={updatePuzzleSliceStyleOnStateChange()}
      style={{ backgroundSize, backgroundImage, backgroundPosition }}
    />
  );
}
